import { every, filter, map, reduce, size, split } from "lodash";
import { getFormValues } from "redux-form";
import { useLocation } from "react-router-dom";
import { useState } from "react";

import { cancelScheduledItems } from "src/redux/reducers";
import { createErrorNotification, createSuccessNotification, translate } from "src/services";
import { getItemSettings } from "src/utils/bulkyItems";
import {
  IconButton,
  IconButtonIcon,
  Separator,
  SeparatorLine,
  TextDisplay,
  Wrapper,
} from "src/styled";
import { SchedulerWizardFormValues } from "src/interfaces";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "../AddressForm";
import CancelRequestItemsModal from "../../modals/CancelRequestItemsModal";
import ItemSummaryCard from "./ItemSummaryCard";

interface Props {
  displayEditButton: boolean;
  isSummary: boolean;
  onEditClick: () => void;
}

const ItemsSummarySection = ({ isSummary, displayEditButton, onEditClick }: Props) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelItemsModalOpen, setIsCancelItemsModalOpen] = useState(false);
  const [canceledItemsIds, setCanceledItemsIds] = useState<number[]>([]);
  const dispatch = useAppDispatch();

  const vendorSettings = useAppSelector((state) => state.citySettings.settings?.vendorSettings);

  const categorySettings = useAppSelector((state) => state.citySettings.settings?.categories);

  const maxItemsPerRequestReached = useAppSelector(
    (state) => state.bulkyItemsScheduler.errors.maxItemsPerRequestReached
  );

  const quantitySettings = useAppSelector(
    (state) => state.citySettings.settings?.vendorSettings?.maxItemsPerRequest
  );

  const confirmationId = useAppSelector((state) => state.bulkyItemsScheduler.confirmationId);

  const { pathname } = useLocation();

  // requestId
  const requestId = split(pathname, "/")[3];

  const { isSaving } = useAppSelector((state) => state.bulkyItemsScheduler);

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const totalPrice = reduce(
    filter(thisFormValues?.items, (i) => !i.isCancelled),
    (sum, item) =>
      sum +
      ((categorySettings &&
        vendorSettings &&
        getItemSettings(item, item.pickupDate, categorySettings)?.price) ||
        0) *
        item.quantity,
    0
  );

  const onCheckboxChange = (index: string) => (value: boolean) => {
    if (value) {
      thisFormValues.items[index].id &&
        setCanceledItemsIds([...canceledItemsIds, thisFormValues.items[index].id as number]);
    } else {
      thisFormValues.items[index].id &&
        setCanceledItemsIds(canceledItemsIds.filter((id) => id !== thisFormValues.items[index].id));
    }
  };

  const handleCancelPickup = () => {
    cancelScheduledItems(
      canceledItemsIds,
      confirmationId || requestId
    )(dispatch)
      .then(() => {
        setIsCancelItemsModalOpen(false);
        setCanceledItemsIds([]);
        setIsCancelling(false);
        createSuccessNotification(translate("alertMessages.cancelSuccess"))(dispatch);
      })
      .catch((e) => {
        setIsCancelItemsModalOpen(false);
        setCanceledItemsIds([]);
        setIsCancelling(false);
        if (e?.response.data?.message) createErrorNotification(e.response.data.message)(dispatch);
        else createErrorNotification(translate("alertMessages.generalError"))(dispatch);
      });
  };

  // if any of the items has a price, then we need to display the price
  const displayPrice = every(thisFormValues?.items, (item) => !!item.pickupDate);

  const allItemCancelled = every(thisFormValues?.items, (item) =>
    item.isCancelled ? item.isCancelled : false
  );

  if (!size(thisFormValues?.items)) return null;

  return (
    <>
      <Wrapper display="flex" justifyContent="space-between" alignItems="center">
        <TextDisplay size="medium" color="grayDarker">
          <b>{translate("wizardPage.bulkItems")}</b>
        </TextDisplay>
        {displayEditButton && (
          <IconButton
            type="button"
            color="gray"
            onClick={onEditClick}
            padding="xxSmall small"
            disabled={isSaving}
          >
            <IconButtonIcon size="medium" icon="edit" customViewBox="0 0 30 32" color="black" />

            <TextDisplay size="xSmall" color="black">
              {translate("common.edit")}
            </TextDisplay>
          </IconButton>
        )}
        {isSummary && !allItemCancelled && (
          <Wrapper display="flex" alignItems="center">
            <IconButton
              type="button"
              color="gray"
              onClick={() => {
                if (!isCancelling) {
                  setIsCancelling(true);
                } else if (!!size(canceledItemsIds)) {
                  setIsCancelItemsModalOpen(true);
                }
              }}
              padding="xxSmall small"
              disabled={(isCancelling && !size(canceledItemsIds)) || isSaving}
            >
              <IconButtonIcon
                size="medium"
                icon="cancelCircle"
                customViewBox="0 0 14 14"
                color="alert"
              />
              <TextDisplay size="xSmall" color="alert">
                {isCancelling
                  ? translate("wizardPage.cancelSelectedItems")
                  : translate("wizardPage.cancelRequest")}
              </TextDisplay>
            </IconButton>
            {isCancelling && (
              <IconButton
                type="button"
                color="gray"
                onClick={() => {
                  setIsCancelling(false);
                  setCanceledItemsIds([]);
                }}
                padding="xxSmall small"
                margin="no small"
                disabled={isSaving}
              >
                <TextDisplay size="no xSmall" color="black">
                  {translate("common.discard")}
                </TextDisplay>
              </IconButton>
            )}
          </Wrapper>
        )}
      </Wrapper>
      <Separator height={10} />
      <Wrapper>
        {map(thisFormValues.items, (item, index) => (
          <ItemSummaryCard
            key={item.name}
            item={item}
            onCheckboxChange={onCheckboxChange(index)}
            isCancelling={isCancelling}
            checkboxValue={item.id && canceledItemsIds.includes(item.id) ? true : false}
          />
        ))}
        {displayPrice && (
          <TextDisplay margin="small no no small" block align="left" size="medium">
            {translate("common.total")}: ${totalPrice}
          </TextDisplay>
        )}
      </Wrapper>

      {maxItemsPerRequestReached && displayEditButton && (
        <TextDisplay margin="small no medium" block color="alert" size="xSmall">
          <em>
            {translate("alertMessages.quantityExceeded", {
              maxQuantity: quantitySettings,
            })}
          </em>
        </TextDisplay>
      )}

      <Separator height={30} />
      <SeparatorLine />
      <Separator height={30} />

      {isCancelItemsModalOpen && (
        <CancelRequestItemsModal
          closeModal={() => setIsCancelItemsModalOpen(false)}
          onCancelRequest={handleCancelPickup}
        />
      )}
    </>
  );
};

export default ItemsSummarySection;
